import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { api, type Lead, type NoteWithLead } from "@/lib/api-client";
import { Header } from "@/components/layout/Header";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { formatTimeAgo } from "@/lib/utils";
import { STATUS_COLORS } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { AddNoteDialog } from "@/components/notes/AddNoteDialog";
import { 
  Plus, 
  Search, 
  StickyNote, 
  Trash2, 
  Pencil, 
  Save, 
  X, 
  ArrowRight,
  MoreHorizontal,
  Phone,
  Clock
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { motion, AnimatePresence } from "framer-motion";

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 15 },
  show: { 
    opacity: 1, 
    scale: 1, 
    y: 0,
    transition: {
      type: "spring" as const,
      stiffness: 300,
      damping: 24
    }
  }
};

type LeadGroup = {
  lead: Lead;
  items: NoteWithLead[];
};

const groupByLead = (items: NoteWithLead[]): LeadGroup[] => {
  const map = new Map<number, LeadGroup>();
  for (const item of items) {
    const existing = map.get(item.lead.id);
    if (existing) existing.items.push(item);
    else map.set(item.lead.id, { lead: item.lead, items: [item] });
  }

  const groups = [...map.values()];
  for (const g of groups) g.items.sort((a, b) => b.note.createdAt - a.note.createdAt);
  groups.sort((a, b) => (b.items[0]?.note.createdAt ?? 0) - (a.items[0]?.note.createdAt ?? 0));
  return groups;
};

export function NotesPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const [me, setMe] = useState<{ isAdmin: boolean } | null>(null);
  const [q, setQ] = useState("");
  const [items, setItems] = useState<NoteWithLead[]>([]);

  const [showAdd, setShowAdd] = useState(false);
  const [addContent, setAddContent] = useState("");
  const [leadPickerQuery, setLeadPickerQuery] = useState("");
  const [leadPickerLoading, setLeadPickerLoading] = useState(false);
  const [leadPickerLeads, setLeadPickerLeads] = useState<Lead[]>([]);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");

  const groups = useMemo(() => groupByLead(items), [items]);

  const load = async (query?: string) => {
    setIsLoading(true);
    setError("");
    try {
      const [meRes, notesRes] = await Promise.all([
        api.getMe(),
        api.listNotesAll({ limit: 100, q: query?.trim().length ? query.trim() : undefined }),
      ]);
      setMe({ isAdmin: meRes.isAdmin });
      setItems(notesRes.notes);
    } catch (e) {
      console.error("Failed to load notes:", e);
      setError(e instanceof Error ? e.message : "Failed to load notes");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void load("");
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => void load(q), 250);
    return () => window.clearTimeout(t);
  }, [q]);

  const openAdd = async () => {
    setShowAdd(true);
    setAddContent("");
    setLeadPickerQuery("");
    setSelectedLead(null);

    setLeadPickerLoading(true);
    try {
      const { chats } = await api.getChats({ limit: 100 });
      setLeadPickerLeads(chats);
    } catch (e) {
      console.error("Failed to load chats:", e);
    } finally {
      setLeadPickerLoading(false);
    }
  };

  const submitAdd = async () => {
    if (!selectedLead) return;
    const content = addContent.trim();
    if (!content) return;
    try {
      await api.addNote(selectedLead.id, content);
      setShowAdd(false);
      await load(q);
    } catch (e) {
      console.error("Failed to add note:", e);
      alert(e instanceof Error ? e.message : "Failed to add note");
    }
  };

  const startEdit = (id: number, current: string) => {
    setEditingId(id);
    setEditValue(current);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const saveEdit = async (leadId: number, noteId: number) => {
    const content = editValue.trim();
    if (!content) return;
    try {
      await api.updateNote(leadId, noteId, content);
      cancelEdit();
      await load(q);
    } catch (e) {
      console.error("Failed to update note:", e);
      alert(e instanceof Error ? e.message : "Failed to update note");
    }
  };

  const deleteNote = async (leadId: number, noteId: number) => {
    if (!confirm("Delete this note?")) return;
    try {
      await api.deleteNote(leadId, noteId);
      await load(q);
    } catch (e) {
      console.error("Failed to delete note:", e);
      alert(e instanceof Error ? e.message : "Failed to delete note");
    }
  };

  return (
    <>
      <Header title="Notes" subtitle="All chats" />

      <div className="p-6 space-y-6">
        {/* Search & Actions */}
        <Card className="border-border/60 shadow-sm">
          <CardContent className="p-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search notes, name, phone..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-10 h-10"
              />
            </div>
            <Button onClick={() => void openAdd()} className="h-10">
              <Plus className="w-4 h-4 mr-2" />
              Add Note
            </Button>
          </CardContent>
        </Card>

        {error && (
          <div className="text-sm text-destructive bg-destructive/10 p-4 rounded-lg border border-destructive/20">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Skeleton className="h-64 w-full rounded-2xl" />
            <Skeleton className="h-64 w-full rounded-2xl" />
            <Skeleton className="h-64 w-full rounded-2xl" />
          </div>
        ) : groups.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center text-muted-foreground bg-card/50 rounded-2xl border border-dashed border-border/60">
            <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mb-4 shadow-sm">
              <StickyNote className="w-8 h-8 text-muted-foreground/50" />
            </div>
            <p className="text-lg font-bold text-foreground">No notes found</p>
            <p className="text-sm mt-1 max-w-[250px]">Try adjusting your search or add a new note for a contact.</p>
          </div>
        ) : (
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start"
          >
            {groups.map((g) => (
              <motion.div key={g.lead.id} variants={itemVariants} className="h-full">
                <Card className="h-full border-border/60 shadow-sm overflow-hidden hover:border-primary/30 transition-all flex flex-col rounded-2xl">
                  {/* Lead Header */}
                  <CardHeader className="p-5 bg-primary/5 border-b border-border/40 shrink-0">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <Link href={`/chats/${g.lead.id}`}>
                          <h3 className="font-bold text-base hover:text-primary transition-colors cursor-pointer flex items-center gap-1.5 truncate">
                            {g.lead.name || "Unknown Contact"}
                            <ArrowRight className="w-3.5 h-3.5 opacity-40 shrink-0" />
                          </h3>
                        </Link>
                        <div className="text-xs font-medium text-muted-foreground/70 mt-0.5 flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {g.lead.phone}
                        </div>
                      </div>
                      <Badge variant="secondary" className={cn("text-[10px] px-2 h-5 uppercase font-bold tracking-wider rounded-full shrink-0", STATUS_COLORS[g.lead.status])}>
                        {g.lead.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  
                  {/* Notes List */}
                  <div className="flex-1 overflow-auto divide-y divide-border/40">
                    {g.items.map(({ note }) => (
                      <div key={note.id} className="p-5 group/note relative hover:bg-muted/10 transition-colors">
                        {editingId === note.id ? (
                          <div className="space-y-4">
                            <Textarea 
                              value={editValue} 
                              onChange={(e) => setEditValue(e.target.value)} 
                              className="min-h-[100px] text-sm resize-none rounded-xl"
                              autoFocus
                            />
                            <div className="flex items-center justify-end gap-2">
                              <Button variant="ghost" size="sm" onClick={cancelEdit} className="h-8 rounded-lg">
                                Cancel
                              </Button>
                              <Button size="sm" onClick={() => void saveEdit(note.leadId, note.id)} className="h-8 rounded-lg px-4">
                                Save Changes
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <div className="flex items-center gap-1.5 text-muted-foreground/60">
                                <Clock className="w-3 h-3" />
                                <span className="text-[10px] font-bold uppercase tracking-widest">
                                  {formatTimeAgo(note.createdAt)}
                                </span>
                              </div>
                              
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-6 w-6 rounded-full text-muted-foreground opacity-0 group-hover/note:opacity-100 transition-opacity focus:opacity-100"
                                  >
                                    <MoreHorizontal className="w-3.5 h-3.5" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-32">
                                  <DropdownMenuItem onClick={() => startEdit(note.id, note.content)} className="cursor-pointer">
                                    <Pencil className="w-3.5 h-3.5 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  {me?.isAdmin && (
                                    <DropdownMenuItem 
                                      onClick={() => void deleteNote(note.leadId, note.id)}
                                      className="text-destructive focus:text-destructive cursor-pointer"
                                    >
                                      <Trash2 className="w-3.5 h-3.5 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                            
                            <p className="text-sm font-medium leading-relaxed text-foreground/80 break-words">
                              {note.content}
                            </p>
                            
                            <div className="mt-3 flex items-center justify-between">
                              <div className="text-[10px] font-bold text-primary/60 bg-primary/5 px-2 py-0.5 rounded-full border border-primary/10">
                                {note.createdBy.split("@")[0]}
                              </div>
                              {note.updatedBy && (
                                <span className="text-[9px] text-muted-foreground/40 italic">Edited</span>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      <AddNoteDialog
        open={showAdd}
        onOpenChange={setShowAdd}
        leads={leadPickerLeads}
        isLoadingLeads={leadPickerLoading}
        query={leadPickerQuery}
        onQueryChange={setLeadPickerQuery}
        selectedLead={selectedLead}
        onSelectLead={setSelectedLead}
        content={addContent}
        onContentChange={setAddContent}
        onSubmit={() => void submitAdd()}
      />
    </>
  );
}
